import React, { useState, useEffect } from 'react';
import { getAuditorByToken, updateAuditorLastAccessed, getAssessments, getTasks, getEvidence, getProfile } from './db';
import { LEVELS } from './data/levels';
import { QUESTIONS } from './data/questions';
import { GAPS } from './data/gaps';
import SPRSScore from './SPRSScore';
import { generateSSP, generatePOAM } from './generatePDF';
import { getEvidenceDownloadUrl } from './db';

const FW_META = {
  cmmc_l1:  { label: 'CMMC Level 1',     scoreLabel: 'SPRS Score',  scoreType: 'sprs'    },
  cmmc_l2:  { label: 'CMMC Level 2',     scoreLabel: 'SPRS Score',  scoreType: 'sprs'    },
  cmmc_l3:  { label: 'CMMC Level 3',     scoreLabel: 'SPRS Score',  scoreType: 'sprs'    },
  nist_171: { label: 'NIST SP 800-171',  scoreLabel: 'Score',       scoreType: 'percent' },
  soc2:     { label: 'SOC 2',            scoreLabel: 'Compliance',  scoreType: 'percent' },
  iso27001: { label: 'ISO 27001',        scoreLabel: 'Compliance',  scoreType: 'percent' },
  hipaa:    { label: 'HIPAA',            scoreLabel: 'Compliance',  scoreType: 'percent' },
  nist_csf: { label: 'NIST CSF 2.0',    scoreLabel: 'Compliance',  scoreType: 'percent' },
  pci_dss:  { label: 'PCI DSS v4.0',    scoreLabel: 'Compliance',  scoreType: 'percent' },
  fedramp:  { label: 'FedRAMP Moderate', scoreLabel: 'Compliance',  scoreType: 'percent' },
};

function fileTypeLabel(ft) {
  if (!ft) return 'FILE';
  const t = ft.toLowerCase();
  if (t.includes('pdf'))                              return 'PDF';
  if (t.includes('word') || t.includes('doc'))       return 'DOC';
  if (t.includes('sheet') || t.includes('excel'))    return 'XLS';
  if (t.includes('image') || t.includes('png') || t.includes('jpg')) return 'IMG';
  if (t.includes('zip'))                             return 'ZIP';
  return 'FILE';
}

function FileTypeBox({ fileType }) {
  const label = fileTypeLabel(fileType);
  const colors = {
    PDF: { bg: 'rgba(224,82,82,0.15)',   color: '#E05252' },
    DOC: { bg: 'rgba(45,125,210,0.15)',  color: '#2D7DD2' },
    XLS: { bg: 'rgba(29,184,122,0.15)', color: '#1DB87A' },
    IMG: { bg: 'rgba(245,166,35,0.15)', color: '#F5A623' },
    ZIP: { bg: 'rgba(124,58,237,0.15)', color: '#7C3AED' },
    FILE:{ bg: 'rgba(90,100,120,0.15)', color: '#8A95A8' },
  };
  const c = colors[label] || colors.FILE;
  return (
    <div style={{
      width: 32, height: 32, borderRadius: 6, background: c.bg,
      border: `1px solid ${c.color}44`, display: 'flex', alignItems: 'center',
      justifyContent: 'center', fontSize: 9, fontWeight: 700,
      color: c.color, fontFamily: 'var(--mono)', letterSpacing: '0.02em', flexShrink: 0,
    }}>
      {label}
    </div>
  );
}

export default function AuditorView({ token }) {
  const [access, setAccess]         = useState(null);
  const [profile, setProfile]       = useState({});
  const [answers, setAnswers]       = useState({});
  const [tasks, setTasks]           = useState([]);
  const [evidence, setEvidence]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [activeTab, setActiveTab]   = useState('overview');
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadError, setDownloadError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Parse framework from URL params if present
  const urlParams  = new URLSearchParams(window.location.search);
  const urlFw      = urlParams.get('fw');

  useEffect(() => {
    loadAuditorData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function loadAuditorData() {
    setLoading(true);
    const accessData = await getAuditorByToken(token);
    if (!accessData) {
      setError('This access link is invalid or has been revoked.');
      setLoading(false);
      return;
    }
    if (new Date(accessData.expires_at) < new Date()) {
      setError('This access link has expired. Please contact the organization for a new link.');
      setLoading(false);
      return;
    }
    setAccess(accessData);

    const [profileData, answersData, tasksData, evidenceData] = await Promise.all([
      getProfile(accessData.owner_id),
      getAssessments(accessData.owner_id),
      getTasks(accessData.owner_id),
      getEvidence(accessData.owner_id),
      updateAuditorLastAccessed(token),
    ]);

    if (profileData) setProfile(profileData);
    if (answersData) setAnswers(answersData);
    if (tasksData)   setTasks(tasksData);
    if (evidenceData) setEvidence(evidenceData);
    setLoading(false);
  }

  async function handleDownload(ev) {
    if (!ev.file_url) return;
    setDownloadError('');
    setDownloadingId(ev.id);
    const { signedUrl, error: dlErr } = await getEvidenceDownloadUrl(ev.file_url);
    setDownloadingId(null);
    if (dlErr || !signedUrl) {
      setDownloadError('Could not generate download link. Please try again.');
      return;
    }
    window.open(signedUrl, '_blank');
  }

  if (loading) return (
    <div style={{
      height: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: 'var(--bg)',
      flexDirection: 'column', gap: 12,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 8,
        background: 'linear-gradient(135deg, #2D7DD2, #1A4E8A)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 14, fontWeight: 700, color: '#fff',
      }}>CQ</div>
      <div style={{ fontSize: 13, color: 'var(--text3)' }}>Loading compliance data...</div>
    </div>
  );

  if (error) return (
    <div style={{
      height: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: 'var(--bg)',
      flexDirection: 'column', gap: 16, padding: 40,
    }}>
      <div style={{
        width: 48, height: 48, borderRadius: '50%',
        background: 'var(--red-bg)', border: '1px solid var(--red)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 22, fontWeight: 700, color: 'var(--red)',
      }}>!</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>Access Denied</div>
      <div style={{ fontSize: 13, color: 'var(--text3)', textAlign: 'center', maxWidth: 400, lineHeight: 1.6 }}>
        {error}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center', maxWidth: 400 }}>
        Contact the organization to request a new access link.
      </div>
    </div>
  );

  const framework  = access?.framework || urlFw || 'cmmc_l2';
  const fwMeta     = FW_META[framework] || FW_META['cmmc_l2'];
  const isCmmc     = ['cmmc_l1', 'cmmc_l2', 'cmmc_l3'].includes(framework);
  const level      = profile.target_level || 2;
  const lv         = LEVELS[level] || LEVELS[2];
  const tabs       = ['overview', 'assessment', 'gaps', 'evidence', 'tasks'];

  const answered  = Object.keys(answers).length;
  const implCount = Object.values(answers).filter(a => a === 'yes').length;
  const partCount = Object.values(answers).filter(a => a === 'partial').length;
  const gapCount  = Object.values(answers).filter(a => a === 'no').length;
  const pctScore  = answered > 0
    ? Math.round(((implCount + partCount * 0.5) / Math.max(answered, 110)) * 100)
    : 0;

  const expiresAt   = access?.expires_at ? new Date(access.expires_at) : null;
  const auditorName = access?.auditor_name || '';
  const auditorFirm = access?.auditor_firm || '';

  function statusColor(pct) {
    return pct >= 80 ? 'var(--green)' : pct >= 50 ? 'var(--amber)' : 'var(--red)';
  }
  function statusLabel(pct) {
    return pct >= 80 ? 'On Track' : pct >= 50 ? 'In Progress' : 'At Risk';
  }
  function priorityColor(p) {
    return p === 'high' ? 'var(--red)' : p === 'med' ? 'var(--amber)' : 'var(--accent)';
  }
  function priorityBg(p) {
    return p === 'high' ? 'var(--red-bg)' : p === 'med' ? 'var(--amber-bg)' : 'var(--blue-bg)';
  }
  function statusStyle(s) {
    const v = (s || '').toLowerCase();
    if (v === 'approved') return { bg: 'var(--green-bg)', color: 'var(--green)', label: 'Approved' };
    if (v === 'rejected') return { bg: 'var(--red-bg)',   color: 'var(--red)',   label: 'Rejected' };
    if (v === 'in_review' || v === 'review') return { bg: 'var(--amber-bg)', color: 'var(--amber)', label: 'In Review' };
    if (v === 'draft')    return { bg: 'var(--surface2)', color: 'var(--text3)', label: 'Draft' };
    return { bg: 'var(--blue-bg)', color: 'var(--accent)', label: 'Pending' };
  }

  const lastAccessed = access?.last_accessed_at ? new Date(access.last_accessed_at) : null;

  return (
    <div style={{ fontFamily: 'var(--font)', background: 'var(--bg)', height: '100vh', overflowY: 'auto' }}>

      {/* Auditor Access Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #1A4E8A 0%, #2D7DD2 100%)',
        padding: '10px 28px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 16 }}>🔍</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>
              Auditor View — Read-Only Access
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)' }}>
              {profile.company_name || 'Defense Contractor'}
              {(auditorName || auditorFirm)
                ? ` · Reviewer: ${[auditorName, auditorFirm].filter(Boolean).join(', ')}`
                : ''}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          {lastAccessed && (
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.65)' }}>
              Last accessed: {lastAccessed.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </div>
          )}
          {expiresAt && (
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.65)' }}>
              Access expires: {expiresAt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
          )}
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 10,
            background: 'rgba(255,255,255,0.15)', color: '#fff',
            border: '1px solid rgba(255,255,255,0.3)',
          }}>
            READ-ONLY
          </span>
        </div>
      </div>

      {/* Header */}
      <div style={{
        background: 'var(--bg2)', borderBottom: '1px solid var(--border)',
        padding: '0 28px', minHeight: 64,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 10, flexWrap: 'wrap', gap: 8, paddingTop: 10, paddingBottom: 10,
      }}>
        {/* Left: branding + company */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: 'linear-gradient(135deg, #2D7DD2, #1A4E8A)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 700, color: '#fff', flexShrink: 0,
          }}>CQ</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>
              {fwMeta.label} Assessment Review
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>
              {profile.company_name || 'Defense Contractor'}
              {profile.assessment_date
                ? ` · ${new Date(profile.assessment_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`
                : ''}
            </div>
          </div>
        </div>

        {/* Right: badges */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {(auditorName || auditorFirm) && (
            <div style={{
              fontSize: 11, color: 'var(--text2)',
              background: 'var(--surface)', border: '1px solid var(--border2)',
              borderRadius: 6, padding: '4px 10px',
            }}>
              {auditorName}{auditorFirm ? ` · ${auditorFirm}` : ''}
            </div>
          )}
          {expiresAt && (
            <div style={{ fontSize: 10, color: 'var(--text3)' }}>
              Access expires {expiresAt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
          )}
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 10,
            background: 'var(--amber-bg)', color: 'var(--amber)',
            border: '1px solid var(--amber)',
          }}>
            Read-Only
          </span>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{
        background: 'var(--bg2)', borderBottom: '1px solid var(--border)',
        padding: '0 28px', display: 'flex', gap: 4, overflowX: 'auto',
        position: 'sticky', top: 64, zIndex: 9,
      }}>
        {tabs.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '12px 16px', fontSize: 12, fontWeight: 500,
              cursor: 'pointer', border: 'none', fontFamily: 'var(--font)',
              background: 'transparent', textTransform: 'capitalize', whiteSpace: 'nowrap',
              color: activeTab === tab ? 'var(--accent)' : 'var(--text3)',
              borderBottom: activeTab === tab ? '2px solid var(--accent)' : '2px solid transparent',
              transition: 'all 0.15s',
            }}
          >
            {tab === 'evidence' ? `Evidence (${evidence.length})` :
             tab === 'tasks'    ? `Tasks (${tasks.filter(t => !t.done).length} open)` :
             tab === 'assessment' ? `Assessment (${answered} answered)` :
             tab === 'gaps'    ? `Gaps (${gapCount})` :
             tab}
          </button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', paddingRight: 4, flexShrink: 0 }}>
          <button
            onClick={() => generateSSP(level, lv, answers, QUESTIONS, tasks, evidence, profile, framework)}
            style={{
              fontSize: 11, fontWeight: 500, padding: '6px 12px', borderRadius: 5,
              cursor: 'pointer', border: '1px solid var(--border2)',
              background: 'var(--surface)', color: 'var(--text2)', fontFamily: 'var(--font)',
            }}
          >Export SSP</button>
          <button
            onClick={() => generatePOAM(tasks, level)}
            style={{
              fontSize: 11, fontWeight: 500, padding: '6px 12px', borderRadius: 5,
              cursor: 'pointer', border: '1px solid var(--border2)',
              background: 'var(--surface)', color: 'var(--text2)', fontFamily: 'var(--font)',
            }}
          >Export POA&amp;M</button>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: 28 }}>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div>
            {/* Company card */}
            <div style={{
              background: 'var(--bg2)', border: '1px solid var(--border)',
              borderRadius: 10, padding: 20, marginBottom: 16,
              display: 'flex', alignItems: 'center', gap: 16,
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: '50%',
                background: 'linear-gradient(135deg, #2D7DD2, #0D3F73)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18, fontWeight: 700, color: '#fff', flexShrink: 0,
              }}>
                {(profile.company_name || 'C').charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>
                  {profile.company_name || 'Defense Contractor'}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                  {profile.cage_code && (
                    <span style={{
                      fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--accent)',
                      background: 'var(--blue-bg)', padding: '2px 8px', borderRadius: 4,
                      border: '1px solid rgba(45,125,210,0.2)',
                    }}>
                      CAGE: {profile.cage_code}
                    </span>
                  )}
                  <span style={{
                    fontSize: 10, fontWeight: 700, color: 'var(--accent)',
                    background: 'var(--blue-bg)', padding: '2px 8px', borderRadius: 4,
                    border: '1px solid rgba(45,125,210,0.2)',
                  }}>
                    {fwMeta.label}
                  </span>
                  {isCmmc && (
                    <span style={{
                      fontSize: 10, color: 'var(--text3)',
                      background: 'var(--surface2)', padding: '2px 8px', borderRadius: 4,
                    }}>
                      Level {level}
                    </span>
                  )}
                  {profile.assessment_date && (
                    <span style={{
                      fontSize: 10, color: 'var(--text3)',
                      background: 'var(--surface2)', padding: '2px 8px', borderRadius: 4,
                    }}>
                      Assessment: {new Date(profile.assessment_date).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Key stats */}
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 12, marginBottom: 16,
            }}>
              {[
                { label: 'Controls Assessed', value: answered, color: 'var(--text)' },
                { label: 'Implemented',        value: implCount, color: 'var(--green)' },
                { label: 'Partial',            value: partCount, color: 'var(--amber)' },
                { label: 'Gaps',               value: gapCount,  color: 'var(--red)' },
              ].map(s => (
                <div key={s.label} style={{
                  background: 'var(--bg2)', border: '1px solid var(--border)',
                  borderRadius: 10, padding: 16, textAlign: 'center',
                }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: s.color, marginBottom: 4 }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Score */}
            {isCmmc ? (
              <div style={{ marginBottom: 16 }}>
                <SPRSScore answers={answers} />
              </div>
            ) : (
              <div style={{
                background: 'var(--bg2)', border: '1px solid var(--border)',
                borderRadius: 10, padding: 20, marginBottom: 16,
                display: 'flex', alignItems: 'center', gap: 20,
              }}>
                <div style={{
                  width: 72, height: 72, borderRadius: '50%', flexShrink: 0,
                  border: `4px solid ${statusColor(pctScore)}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
                }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: statusColor(pctScore) }}>
                    {pctScore}%
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
                    {fwMeta.scoreLabel}: {pctScore}%
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                    {implCount} implemented · {partCount} partial · {gapCount} gaps
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20,
                      background: pctScore >= 80 ? 'var(--green-bg)' : pctScore >= 50 ? 'var(--amber-bg)' : 'var(--red-bg)',
                      color: statusColor(pctScore),
                    }}>
                      {statusLabel(pctScore)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Domain Readiness */}
            {lv?.domains && lv.domains.length > 0 && (
              <div style={{
                background: 'var(--bg2)', border: '1px solid var(--border)',
                borderRadius: 10, padding: 20,
              }}>
                <div style={{
                  fontSize: 13, fontWeight: 600, color: 'var(--text)',
                  marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid var(--border)',
                }}>
                  Domain Readiness
                </div>
                <div style={{
                  display: 'grid', gridTemplateColumns: '1fr 140px 60px 80px 100px',
                  gap: 12, paddingBottom: 10, borderBottom: '1px solid var(--border)',
                  fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em',
                }}>
                  <div>Domain</div><div>Progress</div>
                  <div style={{ textAlign: 'right' }}>Score</div>
                  <div style={{ textAlign: 'right' }}>Controls</div>
                  <div style={{ textAlign: 'right' }}>Status</div>
                </div>
                {lv.domains.map(d => (
                  <div key={d.id} style={{
                    display: 'grid', gridTemplateColumns: '1fr 140px 60px 80px 100px',
                    gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border)',
                    alignItems: 'center',
                  }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 500 }}>{d.name}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{d.id}</div>
                    </div>
                    <div style={{ height: 4, background: 'var(--surface2)', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${d.pct}%`, background: statusColor(d.pct), borderRadius: 2 }} />
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: statusColor(d.pct), textAlign: 'right' }}>{d.pct}%</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', textAlign: 'right' }}>{d.met}/{d.total}</div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{
                        fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 10,
                        background: statusColor(d.pct) === 'var(--green)' ? 'var(--green-bg)' :
                                    statusColor(d.pct) === 'var(--amber)' ? 'var(--amber-bg)' : 'var(--red-bg)',
                        color: statusColor(d.pct),
                      }}>{statusLabel(d.pct)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Assessment Tab */}
        {activeTab === 'assessment' && (
          <div>
            <div style={{
              fontSize: 13, fontWeight: 600, color: 'var(--text)',
              marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span>Assessment Results</span>
              <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 400 }}>Read-only</span>
            </div>

            {/* Filter */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
              {['all', 'yes', 'partial', 'no', 'unanswered'].map(f => (
                <button
                  key={f}
                  onClick={() => setFilterStatus(f)}
                  style={{
                    fontSize: 11, padding: '5px 12px', borderRadius: 5,
                    cursor: 'pointer', border: '1px solid var(--border2)',
                    background: filterStatus === f ? 'var(--accent)' : 'var(--surface)',
                    color: filterStatus === f ? '#fff' : 'var(--text3)',
                    fontFamily: 'var(--font)', transition: 'all 0.15s',
                  }}
                >
                  {f === 'yes' ? 'Implemented' : f === 'no' ? 'Not Implemented' :
                   f === 'unanswered' ? 'Not Answered' :
                   f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>

            {QUESTIONS.filter(q => {
              const ans = answers[q.id];
              if (filterStatus === 'all') return true;
              if (filterStatus === 'unanswered') return !ans;
              return ans === filterStatus;
            }).map((q) => {
              const ans = answers[q.id];
              const colors = {
                yes:     { bg: 'var(--green-bg)', color: 'var(--green)', label: 'Implemented'     },
                partial: { bg: 'var(--amber-bg)', color: 'var(--amber)', label: 'Partial'         },
                no:      { bg: 'var(--red-bg)',   color: 'var(--red)',   label: 'Not Implemented' },
              };
              const c = colors[ans] || { bg: 'var(--surface2)', color: 'var(--text3)', label: 'Not Answered' };
              return (
                <div key={q.id} style={{
                  background: 'var(--bg2)', border: '1px solid var(--border)',
                  borderRadius: 10, padding: 16, marginBottom: 8,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16,
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{
                        fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--accent)',
                        background: 'var(--blue-bg)', padding: '2px 7px', borderRadius: 4,
                      }}>{q.id}</span>
                      <span style={{ fontSize: 10, color: 'var(--text3)' }}>{q.domain} Domain</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.5 }}>{q.text}</div>
                  </div>
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '4px 10px',
                    borderRadius: 6, whiteSpace: 'nowrap', flexShrink: 0,
                    background: c.bg, color: c.color,
                  }}>{c.label}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Gaps Tab */}
        {activeTab === 'gaps' && (
          <div>
            <div style={{
              fontSize: 13, fontWeight: 600, color: 'var(--text)',
              marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span>Gap Analysis</span>
              <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 400 }}>
                {GAPS.length} gaps identified
              </span>
            </div>
            <div style={{
              background: 'var(--bg2)', border: '1px solid var(--border)',
              borderRadius: 10, overflow: 'hidden',
            }}>
              <div style={{
                display: 'grid', gridTemplateColumns: '130px 1fr 80px 130px',
                gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)',
                fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em',
              }}>
                <div>Control ID</div><div>Requirement</div><div>Priority</div><div>Status</div>
              </div>
              {GAPS.map(g => (
                <div key={g.id} style={{
                  display: 'grid', gridTemplateColumns: '130px 1fr 80px 130px',
                  gap: 12, padding: '12px 16px', borderBottom: '1px solid var(--border)',
                  alignItems: 'center', fontSize: 12,
                }}>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)' }}>{g.id}</div>
                  <div>
                    <div style={{ fontWeight: 500 }}>{g.req}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{g.sub}</div>
                  </div>
                  <div>
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 4,
                      fontFamily: 'var(--mono)', textTransform: 'uppercase',
                      background: priorityBg(g.priority), color: priorityColor(g.priority),
                    }}>{g.priority}</span>
                  </div>
                  <div>
                    <span style={{
                      fontSize: 10, padding: '3px 8px', borderRadius: 10,
                      background: 'var(--red-bg)', color: 'var(--red)',
                    }}>{g.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Evidence Tab */}
        {activeTab === 'evidence' && (
          <div>
            <div style={{
              fontSize: 13, fontWeight: 600, color: 'var(--text)',
              marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span>Evidence Locker</span>
              <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 400 }}>
                {evidence.length} files · Read-only
              </span>
            </div>

            {downloadError && (
              <div style={{
                background: 'var(--red-bg)', border: '1px solid var(--red)',
                borderRadius: 6, padding: '10px 14px', marginBottom: 14,
                fontSize: 12, color: 'var(--red)', display: 'flex', justifyContent: 'space-between',
              }}>
                {downloadError}
                <button
                  onClick={() => setDownloadError('')}
                  style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 14 }}
                >
                  &times;
                </button>
              </div>
            )}

            {evidence.length === 0 ? (
              <div style={{
                background: 'var(--bg2)', border: '1px solid var(--border)',
                borderRadius: 10, padding: '40px 20px', textAlign: 'center',
                fontSize: 12, color: 'var(--text3)',
              }}>
                No evidence files have been uploaded yet.
              </div>
            ) : (
              <div style={{
                background: 'var(--bg2)', border: '1px solid var(--border)',
                borderRadius: 10, overflow: 'hidden',
              }}>
                <div style={{
                  display: 'grid', gridTemplateColumns: '40px 1fr 120px 100px 100px 80px 100px 50px',
                  gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--border)',
                  fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em',
                }}>
                  <div></div><div>File</div><div>Control</div><div>Framework</div>
                  <div>Date</div><div>Size</div><div>Status</div><div></div>
                </div>
                {evidence.map(ev => {
                  const sc      = statusStyle(ev.status);
                  const title   = ev.title || ev.name || 'Untitled';
                  const control = ev.control_id || ev.control || '—';
                  const dateStr = ev.created_at
                    ? new Date(ev.created_at).toLocaleDateString()
                    : ev.date || '—';
                  const size = ev.file_size
                    ? (ev.file_size > 1048576
                        ? `${(ev.file_size / 1048576).toFixed(1)} MB`
                        : `${Math.round(ev.file_size / 1024)} KB`)
                    : (ev.size || '—');

                  return (
                    <div key={ev.id} style={{
                      display: 'grid',
                      gridTemplateColumns: '40px 1fr 120px 100px 100px 80px 100px 50px',
                      gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--border)',
                      alignItems: 'center', fontSize: 12,
                    }}>
                      <FileTypeBox fileType={ev.file_type || ev.type} />
                      <div>
                        <div style={{ fontWeight: 500 }}>{title}</div>
                        {ev.uploaded_by && (
                          <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1 }}>{ev.uploaded_by}</div>
                        )}
                        {ev.notes && (
                          <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1 }}>
                            {ev.notes.slice(0, 60)}{ev.notes.length > 60 ? '…' : ''}
                          </div>
                        )}
                      </div>
                      <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)' }}>{control}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)' }}>
                        {ev.framework ? (FW_META[ev.framework]?.label || ev.framework) : '—'}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{dateStr}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>{size}</div>
                      <div>
                        <span style={{
                          fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 10,
                          background: sc.bg, color: sc.color,
                        }}>{sc.label}</span>
                      </div>
                      <div>
                        {ev.file_url && (
                          <button
                            onClick={() => handleDownload(ev)}
                            disabled={downloadingId === ev.id}
                            title="Download file"
                            style={{
                              width: 30, height: 30, borderRadius: 5, border: '1px solid var(--border2)',
                              background: 'var(--surface)', cursor: 'pointer', display: 'flex',
                              alignItems: 'center', justifyContent: 'center', fontSize: 13,
                              color: downloadingId === ev.id ? 'var(--text3)' : 'var(--text2)',
                              fontFamily: 'var(--font)',
                            }}
                          >↓</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Tasks Tab */}
        {activeTab === 'tasks' && (
          <div>
            <div style={{
              fontSize: 13, fontWeight: 600, color: 'var(--text)',
              marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span>Remediation Tasks</span>
              <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 400 }}>
                {tasks.filter(t => !t.done).length} open · {tasks.filter(t => t.done).length} done
              </span>
            </div>
            {tasks.length === 0 ? (
              <div style={{
                background: 'var(--bg2)', border: '1px solid var(--border)',
                borderRadius: 10, padding: '40px 20px', textAlign: 'center',
                fontSize: 12, color: 'var(--text3)',
              }}>
                No remediation tasks have been created yet.
              </div>
            ) : (
              <div style={{
                background: 'var(--bg2)', border: '1px solid var(--border)',
                borderRadius: 10, overflow: 'hidden',
              }}>
                <div style={{
                  display: 'grid', gridTemplateColumns: '1fr 110px 110px 110px 80px',
                  gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--border)',
                  fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em',
                }}>
                  <div>Task</div><div>Priority</div><div>Owner</div><div>Due Date</div><div>Status</div>
                </div>
                {tasks.map(t => (
                  <div key={t.id} style={{
                    display: 'grid', gridTemplateColumns: '1fr 110px 110px 110px 80px',
                    gap: 10, padding: '11px 16px', borderBottom: '1px solid var(--border)',
                    alignItems: 'center', fontSize: 12,
                  }}>
                    <div>
                      <div style={{
                        fontWeight: 500,
                        textDecoration: t.done ? 'line-through' : 'none',
                        opacity: t.done ? 0.5 : 1,
                      }}>{t.name}</div>
                      {t.domain && (
                        <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{t.domain} Domain</div>
                      )}
                    </div>
                    <div>
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                        fontFamily: 'var(--mono)', textTransform: 'uppercase',
                        background: priorityBg(t.priority), color: priorityColor(t.priority),
                      }}>{t.priority}</span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text2)' }}>{t.owner || '—'}</div>
                    <div style={{ fontSize: 11, color: 'var(--text2)', fontFamily: 'var(--mono)' }}>{t.due || '—'}</div>
                    <div>
                      <span style={{
                        fontSize: 10, padding: '2px 8px', borderRadius: 10,
                        background: t.done ? 'var(--green-bg)' : 'var(--amber-bg)',
                        color: t.done ? 'var(--green)' : 'var(--amber)',
                      }}>{t.done ? 'Done' : 'Open'}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>

      {/* Footer */}
      <div style={{
        borderTop: '1px solid var(--border)', padding: '16px 28px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'var(--bg2)', flexWrap: 'wrap', gap: 12,
      }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => generateSSP(level, lv, answers, QUESTIONS, tasks, evidence, profile, framework)}
            style={{
              fontSize: 12, fontWeight: 500, padding: '7px 14px', borderRadius: 6,
              cursor: 'pointer', border: '1px solid var(--accent)',
              background: 'var(--accent)', color: '#fff', fontFamily: 'var(--font)',
            }}
          >
            Download SSP Report
          </button>
          <button
            onClick={() => generatePOAM(tasks, level)}
            style={{
              fontSize: 12, fontWeight: 500, padding: '7px 14px', borderRadius: 6,
              cursor: 'pointer', border: '1px solid var(--border2)',
              background: 'transparent', color: 'var(--text2)', fontFamily: 'var(--font)',
            }}
          >
            Download POA&amp;M
          </button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)' }}>
          Powered by{' '}
          <span style={{ color: 'var(--accent)', fontWeight: 600 }}>TheClearAudit</span>
          {' '}· Read-only auditor view · Access expires {expiresAt ? expiresAt.toLocaleDateString() : '—'}
        </div>
      </div>

    </div>
  );
}
